/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn, Item } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './TransactionMapping';

const TransactionMappingManager: ComponentManagerFn<'TransactionMapping'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'TransactionMapping'>(
      platform,
      'TransactionMapping',
    ),
    type: 'TransactionMapping',
    name: 'Transaction Mapping',
    defaultValue,
    props: {
      childAttribute: {
        aliasType: 'TRANSACTION',
        category: 'Data',
        getAttributeDataTypes: () => [
          'Decimal',
          'Double',
          'Integer',
          'String',
          'UserID',
        ],
        getAlias: (page, item, { pageId }) => {
          const childTxn = page[item.parent] as Item<'Transaction'>;
          return {
            alias: childTxn.props.alias,
            embeddedPageId: pageId,
          };
        },
        label: 'Child Attribute',
        required: true,
        type: 'ViewAttributeChooser',
      },
      parentAttribute: {
        aliasType: 'TRANSACTION',
        category: 'Data',
        getAttributeDataTypes: () => [
          'Decimal',
          'Double',
          'Integer',
          'String',
          'UserID',
        ],
        getAlias: (page, item, { pageId }) => {
          const childTxn = page[item.parent] as Item<'Transaction'>;
          const parentTxn = page[childTxn.parent] as Item<'Transaction'>;
          return {
            alias: parentTxn.props.alias,
            embeddedPageId: pageId,
          };
        },
        label: 'Parent Attribute',
        preventMultiEdit: true,
        type: 'ViewAttributeChooser',
        required: true,
      },
      excludeFromFilter: {
        category: 'Data',
        label: 'Exclude from Filter',
        type: 'boolean',
        helpText: `If checked, this attribute will not be added to the where clause when querying the child store.

Use this option if you want to use this mapping just for copying the value from parent to child row on creation.`,
      },
    },
    getDisplayText: (item) =>
      `${item.props.childAttribute} - ${item.props.parentAttribute}`,
    category: 'Data',
    icon: { iconName: 'arrows-h' },
    description: `**Overview**<br/>
The Transaction Mapping component is the critical link that enforces a "Master-Detail" relationship between two transactions. It tells the system how a Child record (like an "Order Line Item") relates to its Parent record (like a "Sales Order").
This component performs two simultaneous functions:
* **Filtering:** It ensures that when you view an Order, you only see the Line Items for that specific Order.
* **Inheritance:** It ensures that when you create a new Line Item, it automatically inherits the correct Order ID from the parent.

**1. Binding the Relationship**<br/>
To link the records, you must map the common field (foreign key) shared by both transactions.
* **Child Attribute:** Select the field in the Child Transaction (e.g., SalesOrderLine.OrderID).
* **Parent Attribute:** Select the matching field in the Parent Transaction (e.g., SalesOrder.OrderID).
* **Constraint:** Both attributes must share the same Data Type (e.g., both must be Integers or both Strings) to form a valid link.

**2. Controlling Behavior**<br/>
You can refine how this relationship affects data retrieval.
* **Exclude from Filter:**
    * **Unchecked (Default):** The mapping is used for both querying and creating. The system adds a WHERE Child.OrderID = Parent.OrderID clause to the child's query.
    * **Checked:** The mapping is used only for creation. The child query will NOT be filtered by this attribute. (Use Case: Use this when you want to copy a value, like "Default Shipping Address," from the parent to the child for new records, but you still want the child list to show all records regardless of that value).

**3. Integration & Structure**<br/>
* **Placement:** This component must be placed strictly inside a nested Transaction structure: Parent Transaction > Child Transaction > Transaction Mapping.
* **Cardinality:** You can add multiple Transaction Mapping components if the relationship relies on a composite key (e.g., linking by both OrderID AND TenantID).`,
  };
};

export default TransactionMappingManager;
